import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import ManageReceiveRequestModal from '../components/dialogs/manage-requests-dialog';
import { RECEIVE_REQUEST } from '../constants/localstorage';
import NotificationContext from './NotificationContext';

export type RequestDetails = {
  callbackOptions: {
    timestamp: string;
    requesterName: string;
    requesterEmail: string;
    amountRequested: string;
    requestId: string;
    transactionId: string;
    event: string;
    description: string;
    role: string;
  };
  roomId: string;
  senderId: {
    _id: string;
    __v: number;
  };
  eventId: string;
  title: string;
  platformId: string;
  eventMessage: string;
  isSeen: boolean;
  isDeleted: boolean;
  _id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
};

type PendingRequestContextType = {
  openModal: boolean;
  setToggleModal: (isOpen: boolean) => void;
  clearRequest: () => void;
  setRequestDetails: (notification: RequestDetails | null) => void;
  requestDetails: RequestDetails | null;
  currentNotificationId: string | null;
  processNextInQueue: () => void;
};

const PendingRequestsContext = createContext<PendingRequestContextType>({
  openModal: false,
  setToggleModal: () => {},
  clearRequest: () => {},
  requestDetails: null,
  currentNotificationId: null,
  setRequestDetails: () => {},
  processNextInQueue: () => {},
});

export default PendingRequestsContext;

export const PendingRequestsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [openModal, setToggleModal] = useState(false);
  const [requestDetails, setRequestDetails] =
    useState<PendingRequestContextType['requestDetails']>(null);
  const [currentNotificationId, setCurrentNotificationId] = useState<
    string | null
  >(null);

  const {
    receiveRequestQueue,
    removeFromReceiveRequestQueue,
    isAppLoaded,
    setAppLoaded,
  } = useContext(NotificationContext);

  const clearRequest = () => {
    setRequestDetails(null);
    setCurrentNotificationId(null);
    localStorage.removeItem(RECEIVE_REQUEST);
  };

  // Helper to get notification id (id or _id)
  const getNotificationId = (notification: any) =>
    notification.id || notification._id;

  const processNextInQueue = useCallback(() => {
    console.log(
      '[processNextInQueue] receiveRequestQueue:',
      receiveRequestQueue,
    );
    if (receiveRequestQueue.length > 0) {
      const nextNotification = receiveRequestQueue[0];
      const notificationId = getNotificationId(nextNotification);
      console.log(
        '[processNextInQueue] Showing next notification:',
        nextNotification,
      );
      setRequestDetails(nextNotification as unknown as RequestDetails);
      setCurrentNotificationId(notificationId);
      setToggleModal(true);
      console.log(
        '[processNextInQueue] Removing notificationId:',
        notificationId,
      );
      removeFromReceiveRequestQueue(notificationId);
    } else {
      console.log('[processNextInQueue] Queue empty, closing modal');
      setToggleModal(false);
      setRequestDetails(null);
      setCurrentNotificationId(null);
    }
  }, [receiveRequestQueue, removeFromReceiveRequestQueue]);

  // Process queue when it changes and app is loaded
  useEffect(() => {
    if (isAppLoaded && receiveRequestQueue.length > 0 && !openModal) {
      processNextInQueue();
    }
  }, [receiveRequestQueue, isAppLoaded, openModal, processNextInQueue]);

  // Mark app as loaded after initial notifications are processed
  useEffect(() => {
    if (!isAppLoaded) {
      // Mark as loaded after a delay to allow initial notifications to be processed
      const timer = setTimeout(() => {
        setAppLoaded(true);
      }, 2000); // Give time for initial API call and notification processing

      return () => clearTimeout(timer);
    }
  }, [isAppLoaded, setAppLoaded]);

  useEffect(() => {
    const requestDetailsStringified: string | null =
      localStorage.getItem(RECEIVE_REQUEST);
    if (requestDetailsStringified) {
      try {
        const requestDetailsParsed: PendingRequestContextType['requestDetails'] =
          JSON.parse(requestDetailsStringified);

        setRequestDetails(requestDetailsParsed);
        setCurrentNotificationId(requestDetailsParsed?._id || null);
        setToggleModal(true);
      } catch (error) {
        console.log('Unable to parse Receive Request Data');
      }
    }

    window.addEventListener('storage', event => {
      const requestDetailsStringified: string | null =
        localStorage.getItem(RECEIVE_REQUEST);

      if (!requestDetailsStringified) {
        clearRequest();
        setToggleModal(false);
      }
    });

    return () => {
      window.addEventListener('storage', event => {
        const requestDetailsStringified: string | null =
          localStorage.getItem(RECEIVE_REQUEST);

        if (!requestDetailsStringified) {
          clearRequest();
          setToggleModal(false);
        }
      });
    };
  }, []);

  return (
    <PendingRequestsContext.Provider
      value={{
        openModal,
        setToggleModal,
        clearRequest,
        requestDetails,
        currentNotificationId,
        setRequestDetails,
        processNextInQueue,
      }}
    >
      {children}

      {openModal && (
        <ManageReceiveRequestModal
          open={openModal}
          onClose={() => {
            setToggleModal(false);
            // Process next in queue after modal closes
            setTimeout(() => processNextInQueue(), 100);
          }}
        />
      )}
    </PendingRequestsContext.Provider>
  );
};
